import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import TabbedFormLayout from "../../components/TabbedFormLayout/TabbedFormLayout";
import styles from "./UserView.module.scss";

export default function UserViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [appList, setAppList] = useState([]);
  const [organizationList, setOrganizationList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) loadUser(id);
  }, [id]);

  const loadUser = async (userId: string) => {
    try {
      const [userRes, settingsRes, authRes, orgListRes, appListRes] = await Promise.all([
        api.post("/core/users/get", { userId }),
        api.post("/core/settings/get", { userId }),
        api.post("/auth/getauth", { userId }),
        api.get("/core/organization/list"),
        api.get("/core/app/list"),
      ]);

      setUser({
        ...userRes.data,
        ...settingsRes.data,
        ...authRes.data,
      });
      setAppList(appListRes.data.apps || []);
      setOrganizationList(orgListRes.data.organizations || []);
    } catch (err) {
      console.error("Failed to load user:", err);
    } finally {
      setLoading(false);
    }
  };

  const renderRow = (label: string, value: any) => (
    <div className={styles.row}>
      <span className={styles.label}>{label}:</span>
      <span className={styles.value}>{value !== null && value !== "" ? value : "—"}</span>
    </div>
  );

  if (loading) return <p>Loading...</p>;
  if (!user) return <p>User not found.</p>;

  const tabs = [
    {
      label: "General",
      content: (
        <div className={styles.tabContent}>
          {renderRow("First Name", user.firstname)}
          {renderRow("Middle Name", user.middlename)}
          {renderRow("Last Name", user.lastname)}
          {renderRow("Email", user.email)}
          {renderRow("Phone Number", user.phone_number)}
          {renderRow("Title", user.title)}
          {renderRow("User Type", user.usertype)}
          {renderRow("Is Super Admin", user.issuperadmin ? "Yes" : "No")}
        </div>
      ),
    },
    {
      label: "Auth Settings",
      content: (
        <div className={styles.tabContent}>
          {renderRow("Passwordless Enabled", user.passwordless_enabled ? "Yes" : "No")}
          {renderRow("Passwordless Method", user.passwordless_method)}
          {renderRow("Two Factor Enabled", user.two_factor_enabled ? "Yes" : "No")}
          {renderRow("Two Factor Type", user.two_factor_type)}
          {renderRow("Recovery Email Enabled", user.recovery_email_enabled ? "Yes" : "No")}
          {renderRow("Recovery Email", user.recovery_email)}
          {renderRow("Session Invalidated", user.session_invalidated ? "Yes" : "No")}
          {renderRow("Account Locked Until", user.account_locked_until)}
          {renderRow("Password Last Changed", user.password_last_changed)}
        </div>
      ),
    },
    {
      label: "User Settings",
      content: (
        <div className={styles.tabContent}>
          {renderRow("Language Preference", user.language_preference)}
          {renderRow("Timezone", user.timezone)}
          {renderRow("Company Address 1", user.company_address_1)}
          {renderRow("Company Address 2", user.company_address_2)}
          {renderRow("Company Unit", user.company_unit)}
          {renderRow("Company State", user.company_state)}
          {renderRow("Company Postal", user.company_postal)}
          {renderRow("Personal Address 1", user.personal_address_1)}
          {renderRow("Personal Address 2", user.personal_address_2)}
          {renderRow("Personal Unit", user.personal_unit)}
          {renderRow("Personal State", user.personal_state)}
          {renderRow("Personal Postal", user.personal_postal)}
          {renderRow("Shipping Address 1", user.shipping_address_1)}
          {renderRow("Shipping Address 2", user.shipping_address_2)}
          {renderRow("Shipping Unit", user.shipping_unit)}
          {renderRow("Shipping State", user.shipping_state)}
          {renderRow("Shipping Postal", user.shipping_postal)}
        </div>
      ),
    },
    {
      label: "App Access",
      content: (
        <div className={styles.tabContent}>
          {renderRow(
            "Active Organization",
            organizationList.find((org) => org.id === user.activeorganizationid)?.name
          )}
          {renderRow("Is Optenics User", user.is_optenics_user ? "Yes" : "No")}
          {renderRow("Is Portal User", user.is_portal_user ? "Yes" : "No")}
          {renderRow("Is Paid User", user.is_paid_user ? "Yes" : "No")}
          {renderRow("Is Affiliates User", user.is_affiliates_user ? "Yes" : "No")}
          <div className={styles.row}>
            <span className={styles.label}>App Access:</span>
            <div className={styles.appList}>
              {appList
                .filter((app) => user.apps?.includes(app.id))
                .map((app) => (
                  <div key={app.id} className={styles.appItem}>{app.name}</div>
                ))}
            </div>
          </div>
          {renderRow("Notification Preferences", JSON.stringify(user.notification_preferences))}
        </div>
      ),
    },
  ];

  return (
    <TabbedFormLayout
      tabs={tabs}
      onSave={() => {}}
      onCancel={() => navigate("/users")}
      showActions={false}
    />
  );
}
